import { Label } from '@radix-ui/react-label';
import { Box, Card, Checkbox, Flex, Heading, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import {
  CREATE_NEW_LIST_ID,
  PitchListHelper,
  getPitchYearOptions,
} from 'classes/helpers/pitch-list.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { SEARCH_ID } from 'components/sections/pitch-list/store/definitions';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { SCALING_OPTIONS } from 'enums/pitches';
import { t } from 'i18next';
import { DEFAULT_ACCEPT_BTN, IBaseDialog } from 'interfaces/i-dialogs';
import { IScalingConfiguration } from 'interfaces/i-pitch-scaling';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority, PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IPitch, IPitchList, IPitchMetadata } from 'lib_ts/interfaces/pitches';
import { clone, compact } from 'lodash';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { PitchListsService } from 'services/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';

interface IProps extends IBaseDialog {
  title: ReactNode;
  description?: string;

  pitches: Partial<IPitch>[];

  /** ids of lists that should not be shown in the dropdown */
  excludedListIDs?: string[];

  duplicating?: boolean;

  // for ms debug > pitch flow, disallow scaling
  disableScaling?: boolean;

  // should also close the dialog when ready
  onCreated: () => void;

  reloadPitches?: (config: {
    activeList: IPitchList | undefined;
  }) => Promise<void>;

  // e.g. close the ms debug dialog
  onGoToPitchList?: () => void;
}

interface IMetadata extends Partial<IPitchMetadata> {
  listID?: string;
}

/** also used for saving new pitches */
export const CopyPitchesDialog = (props: IProps) => {
  const navigate = useNavigate();
  const current = useAuthStore((s) => s.current);

  const loadingLists = useListLibraryStore((s) => s.loading);
  const lists = useListLibraryStore((s) => s.lists);
  const activeList = useListLibraryStore((s) => s.activeList);

  const [loading, setLoading] = useState(false);
  const [listKey, setListKey] = useState(Date.now());

  const yearOptions = useMemo(() => getPitchYearOptions(), []);

  const firstPitch = useMemo(
    () => (props.pitches.length > 0 ? props.pitches[0] : undefined),
    [props.pitches]
  );

  const [metadata, setMetadata] = useState<IMetadata>({
    name: firstPitch?.name,
    type: firstPitch?.type,
    year: firstPitch?.year,
    listID: firstPitch?._parent_id,
  });

  const mergeMetadata = useCallback(
    (v: Partial<IMetadata>) => setMetadata({ ...metadata, ...v }),
    [metadata]
  );

  const listOptions = useMemo(() => {
    const visible = lists.filter(
      (l) => !props.excludedListIDs || !props.excludedListIDs.includes(l._id)
    );

    return PitchListHelper.getListOptions(visible, current);
  }, [lists, props.excludedListIDs, current]);

  const [showScale, setShowScale] = useState(false);

  // only show breaks scaling if at least one pitch prioritizes breaks
  const hasBreaks = useMemo(
    () =>
      props.pitches.findIndex((p) => p.priority === BuildPriority.Breaks) !==
      -1,
    [props.pitches]
  );

  const hasSpins = useMemo(
    () =>
      props.pitches.findIndex(
        (p) =>
          !p.priority ||
          [BuildPriority.Default, BuildPriority.Spins].includes(p.priority)
      ) !== -1,
    [props.pitches]
  );

  const [scale, setScale] = useState<IScalingConfiguration>({
    speed: 1,
    breakZ: 1,
    breakX: 1,
  });

  const getPitchPayloads = () => {
    const isSingle = props.pitches.length === 1;

    if (isSingle && (!metadata.name || metadata.name.trim().length === 0)) {
      NotifyHelper.error({
        message_md: 'Please provide a name for the pitch and try again.',
      });
      return [];
    }

    return props.pitches.map((p) => {
      const o: Partial<IPitch> = clone(p);
      o._parent_id = metadata.listID;

      // ? this preserves training data for pitches w/ old bs_hash format (lacking priority prefix)
      // provide bs_hash to bypass logic in back-end that would recompute bs_hash
      o.bs_hash = p.bs_hash;

      // only update metadata from form if saving a single pitch
      if (isSingle) {
        o.name = metadata.name?.trim();
        o.type = metadata.type;
        o.year = metadata.year;
      }

      // always set plate_loc_backup based on given traj
      if (p.traj) {
        o.plate_loc_backup = TrajHelper.getPlateLoc(p.traj);
      }

      return o;
    });
  };

  const getRebuiltPitchPayloads = async () => {
    const pitches = getPitchPayloads();

    const output: Partial<IPitch>[] = compact(
      pitches.map((p) => {
        if (!p.bs) {
          console.warn(`Pitch ${p._id} has an empty bs`);
          return undefined;
        }

        if (!p.traj) {
          console.warn(`Pitch ${p._id} has an empty traj`);
          return undefined;
        }

        const scalingResult = PitchDesignHelper.getScaledChars(
          {
            priority: p.priority ?? BuildPriority.Default,
            bs: p.bs,
            traj: p.traj,
            breaks: p.breaks,
          },
          scale
        );

        if (!scalingResult.bs) {
          console.warn(`Pitch ${p._id} has an empty scaled bs`);
          return undefined;
        }

        const newPitch = clone(p);

        delete newPitch._id;

        // from original pitch
        newPitch._original_id = p._id;

        // prepend 'Scaled:' to the name if it doesn't already start with it
        newPitch.name = p.name?.startsWith('Scaled:')
          ? p.name
          : `Scaled: ${p.name}`;
        newPitch.priority = p.priority ?? BuildPriority.Default;

        // from rebuilt chars
        newPitch.plate_loc_backup = TrajHelper.getPlateLoc(scalingResult.traj);

        newPitch.bs = scalingResult.bs;
        newPitch.bs_hash = MachineHelper.getBsHash(
          scalingResult.bs,
          p.priority
        );

        newPitch.traj = scalingResult.traj;

        newPitch.breaks = scalingResult.breaks;
        newPitch.seams = p.seams;

        return newPitch;
      })
    );

    return output;
  };

  return (
    <ErrorBoundary identifier="CopyPitchesDialog">
      <CommonDialog
        identifier={props.identifier}
        width={RADIX.DIALOG.WIDTH.MD}
        title={props.title}
        loading={loading}
        description={props.description}
        content={
          <CommonFormGrid columns={3}>
            {props.pitches.length === 1 && (
              <>
                <CommonTextInput
                  id="name"
                  label="common.pitch-name"
                  name="pitch_name"
                  value={metadata.name ?? ''}
                  disabled={loadingLists}
                  placeholder="Type in new name"
                  onChange={(v) => mergeMetadata({ name: v })}
                />
                <CommonSearchInput
                  id="type"
                  name="type"
                  label="common.pitch-type"
                  options={PITCH_TYPE_FILTER_OPTIONS}
                  values={metadata.type ? [metadata.type] : []}
                  onChange={(v) =>
                    mergeMetadata({
                      type: v[0] as PitchType,
                    })
                  }
                  disabled={loadingLists}
                  labelOptional
                  optional
                  skipSort
                />
                <CommonSelectInput
                  id="year"
                  name="year"
                  label="common.year"
                  options={yearOptions}
                  value={metadata.year}
                  onChange={(v) => mergeMetadata({ year: v })}
                  disabled={loadingLists}
                  labelOptional
                  optional
                />
              </>
            )}

            <Box gridColumn="span 3">
              <CommonSearchInput
                key={listKey}
                id="listID"
                name="listID"
                label="common.pitch-list"
                options={listOptions}
                values={
                  metadata.listID !== SEARCH_ID && metadata.listID
                    ? [metadata.listID]
                    : []
                }
                onChange={(v) => {
                  const id = v[0];

                  if (id === CREATE_NEW_LIST_ID) {
                    useListLibraryStore.setState({
                      dialogManage: {
                        key: Date.now(),
                        dialog: {
                          identifier: 'CopyPitchesCreateListDialog',
                          mode: 'create',
                          onCreated: (created) => {
                            // will preselect the new list
                            mergeMetadata({
                              listID: created._id,
                            });

                            // hide the create dialog
                            useListLibraryStore.setState({
                              dialogManage: undefined,
                            });

                            // reset the search input for the new data
                            setListKey(Date.now());
                          },
                          onClose: () => {
                            useListLibraryStore.setState({
                              dialogManage: undefined,
                            });
                          },
                        },
                      },
                    });
                    return;
                  }

                  mergeMetadata({
                    listID: id,
                  });
                }}
                disabled={loadingLists}
                optional
              />
            </Box>

            {!props.disableScaling && (
              <Box gridColumn="span 3">
                <Card>
                  <Flex gap="1">
                    <Label
                      htmlFor="toggleScale"
                      className="CheckboxWrapperLabel"
                    >
                      <Checkbox
                        id="toggleScale"
                        checked={showScale}
                        onCheckedChange={(v) => setShowScale(v as boolean)}
                      />
                    </Label>
                    <Flex direction="column" gap="3" style={{ width: '100%' }}>
                      <Box>
                        <Heading size="2">Scale Pitches</Heading>
                        <Text size="1" color={RADIX.COLOR.SECONDARY}>
                          Adjust the values of the pitch based on a percentage.
                        </Text>
                      </Box>

                      {showScale && (
                        <>
                          <CommonFormGrid columns={hasBreaks ? 3 : 1}>
                            <Box>
                              <CommonSelectInput
                                id="scale-speed"
                                name="scale-speed"
                                label="common.speed"
                                options={SCALING_OPTIONS}
                                value={scale.speed.toString()}
                                onNumericChange={(v) =>
                                  setScale((prev) => ({ ...prev, speed: v }))
                                }
                                skipSort
                              />
                            </Box>

                            {hasBreaks && (
                              <Box>
                                <CommonSelectInput
                                  id="scale-break-z"
                                  name="scale-break-z"
                                  label="common.v-break"
                                  options={SCALING_OPTIONS}
                                  value={scale.breakZ.toString()}
                                  onNumericChange={(v) =>
                                    setScale((prev) => ({ ...prev, breakZ: v }))
                                  }
                                  skipSort
                                />
                              </Box>
                            )}

                            {hasBreaks && (
                              <Box>
                                <CommonSelectInput
                                  id="scale-break-x"
                                  name="scale-break-x"
                                  label="common.h-break"
                                  options={SCALING_OPTIONS}
                                  value={scale.breakX.toString()}
                                  onNumericChange={(v) =>
                                    setScale((prev) => ({ ...prev, breakX: v }))
                                  }
                                  skipSort
                                />
                              </Box>
                            )}
                          </CommonFormGrid>

                          {hasBreaks && hasSpins && (
                            <CommonCallout
                              size="1"
                              text="Break scaling won't affect spin priority pitches."
                            />
                          )}
                        </>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              </Box>
            )}
          </CommonFormGrid>
        }
        buttons={[
          {
            ...DEFAULT_ACCEPT_BTN,
            onClick: async () => {
              const payloads = showScale
                ? await getRebuiltPitchPayloads()
                : getPitchPayloads();

              if (payloads.length === 0) {
                // don't proceed, there was an error getting payloads
                return;
              }

              const isSingle = payloads.length === 1;

              try {
                /** validate inputs */
                if (!metadata.listID) {
                  NotifyHelper.error({
                    message_md: t(
                      'common.please-select-a-pitch-list-and-try-again'
                    ),
                  });
                  return;
                }

                if (payloads.length === 0) {
                  NotifyHelper.error({
                    message_md: t('common.there-are-no-pitches-to-save'),
                  });
                  return;
                }

                /** should disable accept/cancel buttons until the following is complete */
                setLoading(true);

                const postResult = await PitchListsService.getInstance()
                  .addPitches({
                    listID: metadata.listID,
                    data: payloads,
                  })
                  .finally(() => setLoading(false));

                if (!postResult.success) {
                  NotifyHelper.warning({
                    message_md:
                      postResult.error ??
                      `${
                        payloads.length > 1 ? 'Pitches' : metadata.name
                      } could not be created.`,
                  });
                  return;
                }

                /** update the active pitches to show newly added entries */
                if (
                  activeList &&
                  metadata.listID === activeList._id &&
                  props.reloadPitches
                ) {
                  props.reloadPitches({
                    activeList: activeList,
                  });
                }

                const bsHashes = payloads
                  .map((p) => p.bs_hash as string)
                  .filter((m) => !!m);

                useListLibraryStore.setState((prev) => {
                  if (bsHashes.length === 0) {
                    // no hashes, no change
                    return prev;
                  }

                  if (!metadata.listID) {
                    // no list id, no change
                    return prev;
                  }

                  const nextDict = clone(prev.listSummaryDict);

                  const list = nextDict[metadata.listID];

                  if (!list) {
                    // no list found, no change
                    return prev;
                  }

                  list.bs_hashes.push(...bsHashes);
                  list.total += bsHashes.length;

                  return {
                    listSummaryDict: nextDict,
                  };
                });

                const dupeMsg = `${
                  isSingle ? 'Pitch' : 'Pitches'
                } duplicated successfully.`;

                const scaleMsg = showScale
                  ? `${
                      isSingle ? 'Pitch' : 'Pitches'
                    } scaled based on selected percentages.`
                  : '';

                NotifyHelper.success({
                  message_md: props.duplicating
                    ? `${dupeMsg} ${scaleMsg}`.trim()
                    : t('pu.pitches-created-msg'),
                  buttons: [
                    {
                      label: 'common.go-to-pitch-list',
                      dismissAfterClick: true,
                      onClick: () => {
                        if (!metadata.listID) {
                          return;
                        }

                        // go to the pitch list
                        navigate(paths.pitches.list(metadata.listID));

                        // any other side-effects
                        props.onGoToPitchList?.();
                      },
                    },
                  ],
                });

                props.onCreated?.();
                props.onClose();
              } catch (e) {
                console.error(e);
                NotifyHelper.error({
                  message_md: `${
                    isSingle ? metadata.name : 'Pitches'
                  } could not be created.`,
                });
              }
            },
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
